import type { PublicUser } from "@codvero/shared";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { authApi } from "@/api/auth.ts";
import { Alert, Button, Field, Input, PageTitle } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";
import { AuthLayout } from "./AuthLayout.tsx";
import { useSetAuthUser } from "./session.ts";

/** An account an admin made (or whose password an admin reset): its own password comes before anything else. */
export function FirstPasswordPage({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const router = useRouter();
  const setUser = useSetAuthUser();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", again: "" });
  const mismatch = !!form.again && form.again !== form.newPassword;
  const m = useMutation({
    mutationFn: () => authApi.changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword }),
    onSuccess: () => {
      setUser({ ...user, mustChangePassword: false });
      void router.invalidate();
    },
  });
  return (
    <AuthLayout>
      <PageTitle title={t("firstPassword.title", { name: user.name })} subtitle={t("firstPassword.subtitle")} />
      <form className="mt-4 space-y-3" onSubmit={(e) => { e.preventDefault(); if (!mismatch) m.mutate(); }}>
        <Field label={t("firstPassword.given")}><Input type="password" autoComplete="current-password" required value={form.currentPassword} onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} /></Field>
        <Field label={t("settings.security.newPassword")} hint={t("firstPassword.rule")}><Input type="password" autoComplete="new-password" required minLength={12} value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} /></Field>
        <Field label={t("firstPassword.again")}><Input type="password" autoComplete="new-password" required value={form.again} onChange={(e) => setForm({ ...form, again: e.target.value })} /></Field>
        {mismatch && <Alert tone="warn">{t("firstPassword.mismatch")}</Alert>}
        {m.error && <Alert tone="danger">{toMsg(m.error)}</Alert>}
        <Button type="submit" className="w-full" loading={m.isPending} disabled={mismatch || form.newPassword.length < 12}>{t("firstPassword.save")}</Button>
      </form>
    </AuthLayout>
  );
}
